<template>
  <Modal
    :modal-types="[SAVE_CHART_MODAL, SAVE_CHART_AS_MODAL]"
    font-awesome-icon-class="fas fa-fw fa-download"
    modal-title="Save Chart"
    :should-close-modal-on-background-click="false"
  >
    <div
      v-show="isInitializingModal"
      class="loading-spinner-container"
    ><i class="fad fa-fw fa-spinner-third fa-spin fa-2x"></i></div>
    <form
      v-show="!isInitializingModal"
      class="save-chart-form"
      @submit.prevent="handleSaveChartFormSubmit()"
    >
      <label
        v-show="chartBeingViewed && !isChartBeingViewedReadonly"
        class="sc3-toggle-button sc3-toggle-button-first"
        :class="{
          'sc3-toggle-button-active': saveChartOrMoveChartOrSaveAsNewChart === SAVE_CHART,
          'sc3-toggle-button-right-adjacent-active': saveChartOrMoveChartOrSaveAsNewChart === MOVE_CHART,
          'sc3-toggle-button-disabled': isSavingChart || !chartBeingViewed || isChartBeingViewedReadonly
        }"
      >
        <input
          type="radio"
          name="saveChartOrMoveChartOrSaveAsNewChart"
          v-model="saveChartOrMoveChartOrSaveAsNewChart"
          :value="SAVE_CHART"
          :disabled="isSavingChart || !chartBeingViewed || isChartBeingViewedReadonly"
        />
        Save Chart
      </label>
      <label
        v-show="chartBeingViewed && !isChartBeingViewedReadonly"
        class="sc3-toggle-button"
        :class="{
          'sc3-toggle-button-active': saveChartOrMoveChartOrSaveAsNewChart === MOVE_CHART,
          'sc3-toggle-button-left-adjacent-active': saveChartOrMoveChartOrSaveAsNewChart === SAVE_CHART,
          'sc3-toggle-button-right-adjacent-active': saveChartOrMoveChartOrSaveAsNewChart === SAVE_AS_NEW_CHART,
          'sc3-toggle-button-disabled': isSavingChart || !chartBeingViewed || isChartBeingViewedReadonly
        }"
      >
        <input
          type="radio"
          name="saveChartOrMoveChartOrSaveAsNewChart"
          v-model="saveChartOrMoveChartOrSaveAsNewChart"
          :value="MOVE_CHART"
          :disabled="isSavingChart || !chartBeingViewed || isChartBeingViewedReadonly"
        />
        Move Chart
      </label>
      <label
        v-show="chartBeingViewed && !isChartBeingViewedReadonly"
        class="sc3-toggle-button sc3-toggle-button-last"
        :class="{
          'sc3-toggle-button-active': saveChartOrMoveChartOrSaveAsNewChart === SAVE_AS_NEW_CHART,
          'sc3-toggle-button-left-adjacent-active': saveChartOrMoveChartOrSaveAsNewChart === MOVE_CHART,
          'sc3-toggle-button-disabled': isSavingChart
        }"
      >
        <input
          type="radio"
          name="saveChartOrMoveChartOrSaveAsNewChart"
          v-model="saveChartOrMoveChartOrSaveAsNewChart"
          :value="SAVE_AS_NEW_CHART"
          :disabled="isSavingChart"
        />
        Save As New
      </label>
      <hr
        v-show="chartBeingViewed && !isChartBeingViewedReadonly"
        class="sc3-form-divider"
      />
      <div class="save-chart-ui-container">
        <div v-show="isBasic && saveChartOrMoveChartOrSaveAsNewChart === MOVE_CHART" class="basic-user-move-action-notice-message">
          Heads up! You're limited to just one ChartList as a Basic member so you can't move this chart, but you can create up to 500 lists and save more charts when you upgrade your StockCharts service level.
          <div class="upgrade-button-container">
            <a
              href="/youracct/servicelevel"
              target="_blank"
              class="sc3-button sc3-button-blue"
            >
              Upgrade Now
              <i class="fas fa-arrow-circle-right" aria-hidden="true"></i>
            </a>
          </div>
        </div>
        <label
          v-show="saveChartOrMoveChartOrSaveAsNewChart === SAVE_AS_NEW_CHART || saveChartOrMoveChartOrSaveAsNewChart === MOVE_CHART"
          class="sc3-form-label"
          for="save-chart-modal-chart-list-select-menu"
        >SELECT LIST</label>
        <select
          v-show="saveChartOrMoveChartOrSaveAsNewChart === SAVE_AS_NEW_CHART || saveChartOrMoveChartOrSaveAsNewChart === MOVE_CHART"
          id="save-chart-modal-chart-list-select-menu"
          class="sc3-select-menu"
          v-model="selectedChartListRecid"
          :disabled="isSavingChart || (isBasic && saveChartOrMoveChartOrSaveAsNewChart === MOVE_CHART)"
        >
          <option
            :value="SELECT_A_CHART"
            disabled
          >-- Select a ChartList --</option>
          <option
            v-for="chartList in chartLists"
            :key="chartList.recid"
            :value="chartList.recid"
            :disabled="chartList.isReadonly || (saveChartOrMoveChartOrSaveAsNewChart === MOVE_CHART && chartListContainingChartBeingViewed?.recid === chartList.recid)"
          >{{ chartList.htmlSanitizedName }}</option>
        </select>
        <div
          v-if="isMaximumNumberOfChartsPerChartListReachedMessageDisplayed"
          class="charts-in-chartlist-usage-message"
        >{{ maximumNumberOfChartsPerChartListMessage }}</div>
        <div v-show="!isMaximumNumberOfChartsPerChartListReachedMessageDisplayed">
          <label
            v-show="saveChartOrMoveChartOrSaveAsNewChart === SAVE_CHART || saveChartOrMoveChartOrSaveAsNewChart === MOVE_CHART"
            class="sc3-form-label"
            for="save-chart-modal-save-chart-chart-name-input"
          >CHART NAME</label>
          <input
            v-show="saveChartOrMoveChartOrSaveAsNewChart === SAVE_CHART || saveChartOrMoveChartOrSaveAsNewChart === MOVE_CHART"
            type="text"
            placeholder="Name Your Saved Chart"
            id="save-chart-modal-save-chart-chart-name-input"
            class="sc3-text-input"
            v-model="saveChartChartName"
            autocomplete="off"
            :disabled="isSavingChart || (isBasic && saveChartOrMoveChartOrSaveAsNewChart === MOVE_CHART)"
          />
          <label
            v-show="saveChartOrMoveChartOrSaveAsNewChart === SAVE_AS_NEW_CHART"
            class="sc3-form-label"
            for="save-chart-modal-save-as-new-chart-chart-name-input"
          >CHART NAME</label>
          <input
            v-show="saveChartOrMoveChartOrSaveAsNewChart === SAVE_AS_NEW_CHART"
            type="text"
            placeholder="Name Your Saved Chart"
            id="save-chart-modal-save-as-new-chart-chart-name-input"
            class="sc3-text-input"
            v-model="saveAsNewChartName"
            autocomplete="off"
            :disabled="isSavingChart"
          />
          <div class="advanced-options-button-container">
            <button
              type="button"
              class="sc3-button-link sc3-button-link-dark-grey sc3-button-content-visibility-sliding-toggle"
              :class="{ 'sc3-button-content-visibility-sliding-toggle-open': isAdvancedOptionsOpen }"
              @click="toggleAdvancedOptionsMenu()"
            >
              <i class="fas fa-fw fa-caret-right"></i>
              &nbsp;Advanced Options
            </button>
          </div>
          <div
            class="advanced-options-container"
            ref="advancedOptionsContainerNode"
          >
            <label
              v-show="saveChartOrMoveChartOrSaveAsNewChart === SAVE_CHART || saveChartOrMoveChartOrSaveAsNewChart === MOVE_CHART"
              class="sc3-form-label"
              for="save-chart-modal-save-chart-chart-comment-textarea"
            >COMMENTS (OPTIONAL)</label>
            <textarea
              v-show="saveChartOrMoveChartOrSaveAsNewChart === SAVE_CHART || saveChartOrMoveChartOrSaveAsNewChart === MOVE_CHART"
              placeholder="Add a comment about this chart"
              id="save-chart-modal-save-chart-chart-comment-textarea"
              class="sc3-textarea sc3-textarea-resizeable"
              v-model="saveChartChartComments"
              :disabled="isSavingChart || (isBasic && saveChartOrMoveChartOrSaveAsNewChart === MOVE_CHART)"
            ></textarea>
            <label
              v-show="saveChartOrMoveChartOrSaveAsNewChart === SAVE_AS_NEW_CHART"
              class="sc3-form-label"
              for="save-chart-modal-save-as-new-chart-chart-comment-textarea"
            >COMMENTS (OPTIONAL)</label>
            <textarea
              v-show="saveChartOrMoveChartOrSaveAsNewChart === SAVE_AS_NEW_CHART"
              placeholder="Add a comment about this chart"
              id="save-chart-modal-save-as-new-chart-chart-comment-textarea"
              class="sc3-textarea sc3-textarea-resizeable"
              v-model="saveAsNewChartComments"
              :disabled="isSavingChart"
            ></textarea>
          </div>
        </div>
      </div>
      <hr class="sc3-form-divider" />
      <div v-show="errorMessage" class="error-message">{{ errorMessage }}</div>
      <button
        type="submit"
        class="sc3-button sc3-button-green"
        :disabled="isSavingChart || (isBasic && saveChartOrMoveChartOrSaveAsNewChart === MOVE_CHART)"
      >
        <i v-show="!isSavingChart" class="fas fa-fw fa-check-circle"></i>
        <i v-show="isSavingChart" class="fad fa-fw fa-spinner-third fa-spin"></i>
        &nbsp;{{ submitButtonText }}
      </button>
      <button
        type="button"
        class="sc3-button-link"
        @click="closeModal()"
      >
        Close
      </button>
    </form>
  </Modal>
</template>

<script lang="ts" setup>
  import { ref, computed, watch, onMounted } from 'vue';
  import { storeToRefs } from 'pinia';
  import { useChartStore } from '@/stores/chartStore';
  import { useLayoutStore } from '@/stores/layoutStore';
  import { useSavedChartsStore } from '@/stores/savedChartsStore';
  import { useActionResultMessages } from '@/composables/useActionResultMessages';
  import { useAuthentication } from '@/composables/useAuthentication';
  import { useChartComments } from '@/composables/useChartComments';
  import { useModals } from '@/composables/useModals';
  import { useSymbolInformation } from '@/composables/useSymbolInformation';
  import { useWorkbenchSelections } from '@/composables/useWorkbenchSelections';
  import { useLoadChartsAndChartStyles } from '@/composables/useLoadChartsAndChartStyles';
  import { usePerfChartSymbols } from '@/composables/usePerfChartSymbols';
  import { useAccountLevelsMaximumLimits } from '@/composables/useAccountLevelsMaximumLimits';
  import { saveChart } from '@/composables/saveChart';
  import { updateChart } from '@/composables/updateChart';
  import { getSharpCharts2ChartImageUrlData } from '@/composables/getSharpCharts2ChartImageUrlData';
  import { refreshChartLists } from '@/composables/refreshChartLists';
  import { loadChartsForChartList } from '@/composables/loadChartsForChartList';
  import { INVALID_CHART_RECID, isValidChartRecid } from '@/composables/isValidChartRecid';
  import { getChart } from '@/composables/getChart';
  import { moveChart } from '@/composables/moveChart';
  import { INVALID_CHART_LIST_RECID, isValidChartListRecid } from '@/composables/isValidChartListRecid';
  import { INVALID_CHART_LIST_LIST_NUM } from '@/composables/isValidChartListListNum';
  import { waitForDomToRepaint } from '@/utils/waitForDomToRepaint';
  import { areObjectsEqual } from '@/utils/areObjectsEqual';
  import { getStyleUrlSearchParams } from '@/utils/getStyleUrlSearchParams';
  import Modal from '@/components/modals/Modal.vue';
  import { CHART_TYPE_OPTION_PERF_CHART } from '@/enums/chart-type-options';
  import { SymbolInformation } from '@/interfaces/SymbolInformation';
  import { ChartList } from '@/interfaces/chartList';

  const chartStore = useChartStore();
  const layoutStore = useLayoutStore();
  const savedChartsStore = useSavedChartsStore();
  const { recidOfChartBeingViewed, symbol } = storeToRefs(chartStore);
  const { modalType } = storeToRefs(layoutStore);
  const { chartLists } = storeToRefs(savedChartsStore);
  const { addGreenActionResultMessage, addRedActionResultMessage } = useActionResultMessages();
  const { temporaryChartComments, clearTemporaryChartComments } = useChartComments();
  const { isBasic } = useAuthentication();
  const { SAVE_CHART_MODAL, SAVE_CHART_AS_MODAL, closeModal } = useModals();
  const { getSymbolInformation } = useSymbolInformation();
  const {
    selectedChartListRecid: selectedChartListRecidOnWorkbench,
    chartListContainingChartBeingViewed,
    chartBeingViewed,
    isChartBeingViewedReadonly
  } = useWorkbenchSelections();
  const { loadChart } = useLoadChartsAndChartStyles();
  const { getPerfChartSymbolsString } = usePerfChartSymbols();
  const {
    isAccountLevelsMaximumLimitsInitialized,
    maximumNumberOfChartsPerChartListAllowed
  } = useAccountLevelsMaximumLimits();
  const SELECT_A_CHART = INVALID_CHART_LIST_RECID;
  const SAVE_CHART = 'saveChart';
  const MOVE_CHART = 'moveChart';
  const SAVE_AS_NEW_CHART = 'saveAsNewChart';
  const isInitializingModal = ref(false);
  const isSavingChart = ref(false);
  const saveChartOrMoveChartOrSaveAsNewChart = ref(SAVE_AS_NEW_CHART);
  const selectedChartListRecid = ref(SELECT_A_CHART);
  const saveChartChartName = ref('');
  const saveAsNewChartName = ref('');
  const saveChartChartComments = ref('');
  const saveAsNewChartComments = ref('');
  const errorMessage = ref('');
  const isAdvancedOptionsOpen = ref(false);
  const advancedOptionsContainerNode = ref<HTMLDivElement>();
  const isAdvancedOptionsContainerTransitioning = ref(false);
  let lastModalOpenedTime = -1;
  let lastSymbolChangedTime = -1;
  let lastRecidOfChartBeingViewedChangedTime = -1;

  const maximumNumberOfChartsPerChartListMessage = computed(() => {
    return `The selected ChartList has reached the maximum capacity of ${ maximumNumberOfChartsPerChartListAllowed.value } charts.`;
  });

  const submitButtonText = computed(() => {
    if (saveChartOrMoveChartOrSaveAsNewChart.value === MOVE_CHART) {
      return 'Move Chart';
    }

    if (saveChartOrMoveChartOrSaveAsNewChart.value === SAVE_CHART) {
      return 'Save Chart';
    }

    return 'Save New Chart';
  });

  const selectedChartList = computed<ChartList | null>(() => {
    if (!isValidChartListRecid(selectedChartListRecid.value)) {
      return null;
    }

    return savedChartsStore.chartLists.find(chartList => chartList.recid === selectedChartListRecid.value) ?? null;
  });

  const hasReachedMaximumNumberOfChartsPerChartList = computed(() => {
    const chartList = selectedChartList.value;

    return (
      isAccountLevelsMaximumLimitsInitialized.value &&
      !!chartList &&
      !chartList.isReadonly &&
      chartList.charts.length >= maximumNumberOfChartsPerChartListAllowed.value &&
      maximumNumberOfChartsPerChartListAllowed.value > 0
    );
  });

  const isMaximumNumberOfChartsPerChartListReachedMessageDisplayed = computed(() => {
    return (
      (saveChartOrMoveChartOrSaveAsNewChart.value === SAVE_AS_NEW_CHART || saveChartOrMoveChartOrSaveAsNewChart.value === MOVE_CHART) &&
      hasReachedMaximumNumberOfChartsPerChartList.value
    );
  });

  interface SaveChartModalState {
    modalType: string;
    lastModalOpenedTime: number;
    symbol: string;
    lastSymbolChangedTime: number;
    recidOfChartBeingViewed: number;
    recidOfLoadSymbolOnlyChartBeingViewed: number;
    lastRecidOfChartBeingViewedChangedTime: number;
  };

  function toggleAdvancedOptionsMenu() {
    if (
      isAdvancedOptionsContainerTransitioning.value ||
      !advancedOptionsContainerNode.value
    ) {
      return;
    }

    isAdvancedOptionsContainerTransitioning.value = true;
    isAdvancedOptionsOpen.value = !isAdvancedOptionsOpen.value;

    if (isAdvancedOptionsOpen.value) {
      expandContents();
    } else {
      collapseContents();
    }
  }

  async function collapseContents() {
    if (!advancedOptionsContainerNode.value) {
      return;
    }

    const scrollHeight = advancedOptionsContainerNode.value.scrollHeight;
    const transition = advancedOptionsContainerNode.value.style.transition;
    advancedOptionsContainerNode.value.style.transition = '';

    await waitForDomToRepaint();

    if (!advancedOptionsContainerNode.value) {
      return;
    }

    advancedOptionsContainerNode.value.style.maxHeight = scrollHeight + 'px';
    advancedOptionsContainerNode.value.style.transition = transition;

    await waitForDomToRepaint();

    if (!advancedOptionsContainerNode.value) {
      return;
    }

    advancedOptionsContainerNode.value.style.maxHeight = '0px';

    advancedOptionsContainerNode.value.addEventListener('transitionend', expandTransitionCallback);
  }

  function expandContents() {
    if (!advancedOptionsContainerNode.value) {
      return;
    }

    const scrollHeight = advancedOptionsContainerNode.value.scrollHeight;
    advancedOptionsContainerNode.value.style.maxHeight = scrollHeight + 'px';

    advancedOptionsContainerNode.value.addEventListener('transitionend', expandTransitionCallback);
  }

  async function expandTransitionCallback() {
    if (!advancedOptionsContainerNode.value) {
      return;
    }

    advancedOptionsContainerNode.value.removeEventListener('transitionend', expandTransitionCallback);

    if (advancedOptionsContainerNode.value.style.maxHeight !== '0px') {
      advancedOptionsContainerNode.value.style.maxHeight = '';
    }

    await waitForDomToRepaint();

    isAdvancedOptionsContainerTransitioning.value = false;
  }

  async function handleSaveChartFormSubmit() {
    const isUpdatingChart = saveChartOrMoveChartOrSaveAsNewChart.value === SAVE_CHART;
    const isMovingChart = saveChartOrMoveChartOrSaveAsNewChart.value === MOVE_CHART;
    const isSavingNewChart = saveChartOrMoveChartOrSaveAsNewChart.value === SAVE_AS_NEW_CHART;

    if (
      isInitializingModal.value ||
      isSavingChart.value ||
      (modalType.value !== SAVE_CHART_MODAL && modalType.value !== SAVE_CHART_AS_MODAL) ||
      (!isUpdatingChart && !isMovingChart && !isSavingNewChart)
    ) {
      return;
    }

    isSavingChart.value = true;

    clearErrorMessage();

    const chartName = isSavingNewChart ? saveAsNewChartName.value.trim() : saveChartChartName.value.trim();
    const chartComments = isSavingNewChart ? saveAsNewChartComments.value.trim() : saveChartChartComments.value.trim();
    let chartListListNum = INVALID_CHART_LIST_LIST_NUM;
    const modalState = getModalState();

    if (isMovingChart || isSavingNewChart) {
      const chartList = selectedChartList.value;

      if (!chartList) {
        errorMessage.value = 'Please select a ChartList.';
        isSavingChart.value = false;
        return;
      }

      if (chartList.isReadonly) {
        errorMessage.value = 'This ChartList is read-only. Please select a different ChartList.';
        isSavingChart.value = false;
        return;
      }

      if (hasReachedMaximumNumberOfChartsPerChartList.value) {
        errorMessage.value = maximumNumberOfChartsPerChartListMessage.value;
        isSavingChart.value = false;
        return;
      }

      chartListListNum = chartList.listNum;
    }

    if (chartName.length < 1 || chartName.length > 100) {
      errorMessage.value = 'Chart name must be between 1 and 100 characters.';
      isSavingChart.value = false;
      return;
    }

    if (chartComments.length > 2048) {
      errorMessage.value = 'Chart comments must be between 0 and 2048 characters.';
      isSavingChart.value = false;
      return;
    }

    const chartBeingUpdated = await getChart(recidOfChartBeingViewed.value, true);

    if (hasModalStateChanged(modalState)) {
      return;
    }

    if (isMovingChart && chartBeingUpdated?.chartListListNum === chartListListNum) {
      errorMessage.value = 'The selected ChartList already contains this chart.';
      isSavingChart.value = false;
      return;
    }

    if (isUpdatingChart && isChartBeingViewedReadonly.value) {
      errorMessage.value = 'The current chart belongs to a read-only ChartList.';
      isSavingChart.value = false;
      return;
    }

    if (isUpdatingChart) {
      chartListListNum = chartBeingUpdated?.chartListListNum ?? chartListListNum;
    }

    const annotations = chartBeingUpdated?.annotations ?? '';
    let chartRecid = chartBeingUpdated?.recid ?? INVALID_CHART_RECID;
    const symbolCopy = symbol.value;

    try {
      await getSymbolInformation(symbolCopy);
    } catch (e) {
      window.sc3WorkbenchLogger.error(`handleSaveChartFormSubmit failed in SaveChartModal - error occurred while getting symbol information for symbol: ${symbolCopy}.`, e);

      if (!hasModalStateChanged(modalState)) {
        addRedActionResultMessage('Failed to save chart. Please try again.');
        closeModal();
      }

      return;
    }

    if (hasModalStateChanged(modalState)) {
      return;
    }

    let styleUrlSearchParams;

    try {
      const sharpCharts2ChartImageUrlData = await getSharpCharts2ChartImageUrlData(true);

      styleUrlSearchParams = getStyleUrlSearchParams(sharpCharts2ChartImageUrlData);
    } catch (e) {
      window.sc3WorkbenchLogger.error('handleSaveChartFormSubmit failed in SaveChartModal - error occurred while getting SharpCharts2 chart image url data.', e);

      if (!hasModalStateChanged(modalState)) {
        errorMessage.value = 'Failed to save chart. Please try again.';
        isSavingChart.value = false;
      }

      return;
    }

    if (hasModalStateChanged(modalState)) {
      return;
    }

    try {
      if (isMovingChart) {
        if (!isValidChartRecid(chartRecid)) {
          throw new Error(`Unable to move existing chart - invalid chart recid: ${chartRecid}.`);
        }

        await moveChart(chartRecid, chartListListNum);
      }

      if (isSavingNewChart) {
        const rawChart = await saveChart({
          chartListListNum,
          symbol: symbolCopy,
          chartName,
          comments: chartComments,
          annotations,
          styleUrlSearchParams
        });

        chartRecid = Number(rawChart.recID);
      } else {
        if (!isValidChartRecid(chartRecid)) {
          throw new Error(`Unable to update existing chart - invalid chart recid: ${chartRecid}.`);
        }

        await updateChart(chartRecid, {
          chartName,
          comments: chartComments,
          styleUrlSearchParams
        });
      }

      await refreshChartLists();
      await loadChartsForChartList(undefined, chartListListNum, true);

      addGreenActionResultMessage('Success! Your chart has been saved!');

      if (hasModalStateChanged(modalState)) {
        return;
      }

      closeModal();

      await loadChart(chartRecid);
    } catch (e) {
      window.sc3WorkbenchLogger.error('handleSaveChartFormSubmit failed in SaveChartModal - error occurred while saving/moving/updating chart.', e);

      if (!hasModalStateChanged(modalState)) {
        addRedActionResultMessage('Failed to save chart. Please try again.');
        closeModal();
      }
    }
  }

  function getModalState(): SaveChartModalState {
    return {
      modalType: modalType.value,
      lastModalOpenedTime,
      symbol: symbol.value,
      lastSymbolChangedTime,
      recidOfChartBeingViewed: recidOfChartBeingViewed.value,
      recidOfLoadSymbolOnlyChartBeingViewed: chartStore.recidOfLoadSymbolOnlyChartBeingViewed,
      lastRecidOfChartBeingViewedChangedTime
    };
  }

  function hasModalStateChanged(modalState: SaveChartModalState) {
    const currentModalState = getModalState();

    return !areObjectsEqual(modalState, currentModalState);
  }

  function resetModalState() {
    isInitializingModal.value = false;
    isSavingChart.value = false;
    saveChartOrMoveChartOrSaveAsNewChart.value = SAVE_AS_NEW_CHART;
    selectedChartListRecid.value = SELECT_A_CHART;
    saveChartChartName.value = '';
    saveAsNewChartName.value = '';
    saveChartChartComments.value = '';
    saveAsNewChartComments.value = '';
    isAdvancedOptionsOpen.value = false;
    isAdvancedOptionsContainerTransitioning.value = false;

    if (advancedOptionsContainerNode.value) {
      advancedOptionsContainerNode.value.style.maxHeight = '0px';
    }

    clearErrorMessage();
  }

  async function handleSaveChartModalOpened() {
    resetModalState();

    isInitializingModal.value = true;
    const modalState = getModalState();
    const symbolCopy = symbol.value;

    const [
      symbolInformation
    ] = await Promise.all([
      getSymbolInformation(symbolCopy).catch((e) => {
        window.sc3WorkbenchLogger.debug(`Error occurred while getting symbol information for symbol: ${symbolCopy} while SaveChartModal is being opened.`, e);

        return undefined;
      }),
      refreshChartLists(),
      getChart(recidOfChartBeingViewed.value, true)
    ]);

    if (hasModalStateChanged(modalState)) {
      return;
    }

    prefillModalUIFields(symbolInformation);

    isInitializingModal.value = false;

    await waitForDomToRepaint();

    if (hasModalStateChanged(modalState)) {
      return;
    }

    if (
      !isAdvancedOptionsOpen.value &&
      (saveAsNewChartComments.value || saveChartChartComments.value)
    ) {
      toggleAdvancedOptionsMenu();
    }
  }

  function handleSaveChartModalClosed() {
    resetModalState();
  }

  function prefillModalUIFields(symbolInformation?: SymbolInformation) {
    if (chartBeingViewed.value && isChartBeingViewedReadonly.value) {
      saveAsNewChartName.value = chartBeingViewed.value.name;
    } else if (chartStore.chartType === CHART_TYPE_OPTION_PERF_CHART.value) {
      saveAsNewChartName.value = getPerfChartSymbolsString(chartStore.perfChartSymbols, chartStore.symbol, true) + ' - PerfChart';
    } else {
      const companyName = symbolInformation?.name ? symbolInformation.name : '';
      saveAsNewChartName.value = companyName ? `${symbol.value} - ${companyName}` : symbol.value;
    }

    saveAsNewChartComments.value = temporaryChartComments.value;

    if (!temporaryChartComments.value && chartBeingViewed.value && isChartBeingViewedReadonly.value) {
      saveAsNewChartComments.value = chartBeingViewed.value.comments;
    }

    if (!chartBeingViewed.value || isChartBeingViewedReadonly.value) {
      return;
    }

    if (modalType.value !== SAVE_CHART_AS_MODAL) {
      saveChartOrMoveChartOrSaveAsNewChart.value = SAVE_CHART;
    }

    saveChartChartName.value = chartBeingViewed.value.name;
    saveChartChartComments.value = temporaryChartComments.value ? temporaryChartComments.value : chartBeingViewed.value.comments;
  }

  async function handleSymbolChanged() {
    lastSymbolChangedTime = Date.now();

    handleSaveChartModalClosed();

    if (modalType.value !== SAVE_CHART_MODAL && modalType.value !== SAVE_CHART_AS_MODAL) {
      return;
    }

    clearTemporaryChartComments();
    await handleSaveChartModalOpened();
  }

  async function handleRecidOfChartBeingViewedChanged() {
    lastRecidOfChartBeingViewedChangedTime = Date.now();

    handleSaveChartModalClosed();

    if (modalType.value !== SAVE_CHART_MODAL && modalType.value !== SAVE_CHART_AS_MODAL) {
      return;
    }

    clearTemporaryChartComments();
    await handleSaveChartModalOpened();
  }

  async function handleModalTypeChanged(newModalType: string, previousModalType: string) {
    const wasViewingSaveChartModal = previousModalType === SAVE_CHART_MODAL || previousModalType === SAVE_CHART_AS_MODAL;
    const isNowViewingSaveChartModal = newModalType === SAVE_CHART_MODAL || newModalType === SAVE_CHART_AS_MODAL;

    if (wasViewingSaveChartModal === isNowViewingSaveChartModal) {
      return;
    }

    if (!isNowViewingSaveChartModal) {
      handleSaveChartModalClosed();
      clearTemporaryChartComments();
      return;
    }

    lastModalOpenedTime = Date.now();

    await handleSaveChartModalOpened();
  }

  function initializeSelectedChartListRecid() {
    if (selectedChartList.value) {
      return;
    }

    if (isValidChartListRecid(selectedChartListRecidOnWorkbench.value)) {
      selectedChartListRecid.value = selectedChartListRecidOnWorkbench.value;
      return;
    }

    if (chartLists.value[0] && isValidChartListRecid(chartLists.value[0].recid)) {
      selectedChartListRecid.value = chartLists.value[0].recid;
      return;
    }

    selectedChartListRecid.value = SELECT_A_CHART;
  }

  async function loadChartsForSelectedChartList() {
    if (!isValidChartListRecid(selectedChartListRecid.value)) {
      return;
    }

    await loadChartsForChartList(selectedChartListRecid.value, undefined, true);
  }

  function handleChartBeingViewedChanged() {
    if (
      (!chartBeingViewed.value || isChartBeingViewedReadonly.value) &&
      saveChartOrMoveChartOrSaveAsNewChart.value === SAVE_CHART
    ) {
      saveChartOrMoveChartOrSaveAsNewChart.value = SAVE_AS_NEW_CHART;
    }
  }

  function clearErrorMessage() {
    errorMessage.value = '';
  }

  watch(chartLists, initializeSelectedChartListRecid);
  watch(chartBeingViewed, handleChartBeingViewedChanged);
  watch(isChartBeingViewedReadonly, handleChartBeingViewedChanged);
  watch(saveChartOrMoveChartOrSaveAsNewChart, clearErrorMessage);
  watch(selectedChartListRecid, clearErrorMessage);
  watch(selectedChartListRecid, loadChartsForSelectedChartList);
  watch(saveChartChartName, clearErrorMessage);
  watch(saveAsNewChartName, clearErrorMessage);
  watch(saveChartChartComments, clearErrorMessage);
  watch(saveAsNewChartComments, clearErrorMessage);
  watch(symbol, handleSymbolChanged);
  watch(recidOfChartBeingViewed, handleRecidOfChartBeingViewedChanged);
  watch(modalType, handleModalTypeChanged);

  onMounted(resetModalState);
</script>

<style scoped>
  .loading-spinner-container,
  .save-chart-form {
    margin: 0px;
    padding: 0px;
    text-align: center;
  }

  .advanced-options-button-container {
    text-align: left;
    margin: 0px 0px 10px 0px;
  }

  .advanced-options-container {
    transition: max-height 200ms ease-out;
    overflow-y: hidden;
  }

  .charts-in-chartlist-usage-message,
  .error-message {
    margin: 0px 0px 8px 0px;
    font-size: 14px;
    font-weight: 700;
    border-radius: 4px;
    line-height: 1.5;
    padding: 5px;
    border-width: 1px;
    border-style: solid;
  }

  .charts-in-chartlist-usage-message {
    border-color: #faebcc;
    color: #8a6d3b;
    background-color: #fff3cd;
  }

  .error-message {
    border-color: #f5c6cb;
    color: #721c24;
    background-color: #f8d7da;
  }

  .basic-user-move-action-notice-message {
    margin: 15px 0px;
    padding: 15px;
    font-size: 16px;
    color: #31708f;
    background-color: #d9edf7;
    border-color: #bce8f1;
    text-align: center;
    line-height: 1.50;
    border-radius: 4px;
    font-weight: 700;
  }

  .basic-user-move-action-notice-message .upgrade-button-container {
    margin-top: 6px;
  }
</style>
